import React, { useState } from "react";
import { DeleteOutline } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import { useLayerPanelContext } from "fond/project/panels/InputPanel/LayerPanelContext";
import { ConfirmModal, StackedNavigationButton } from "fond/widgets";

interface LayerDeleteActionProps {
  onDelete: () => void | Promise<void>;
  disabled?: boolean;
  confirmHeader?: string;
  confirmContent?: React.ReactNode;
}

/**
 * `LayerPanel` action for deleting the layer's data, after confirmation.
 */
const LayerDeleteAction: React.FC<LayerDeleteActionProps> = ({ onDelete, disabled = false, confirmHeader, confirmContent }) => {
  const { title, layerKey } = useLayerPanelContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setShowConfirm(false);
    } catch {
      enqueueSnackbar(`Failed to delete ${title.toLowerCase()}.`, { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <StackedNavigationButton
        data-testid={`delete-${layerKey}-button`}
        icon={<DeleteOutline />}
        title="Delete"
        subtitle="Remove existing data"
        onClick={() => setShowConfirm(true)}
        disabled={disabled}
      />
      <ConfirmModal
        open={showConfirm}
        header={confirmHeader ?? `Delete ${title.toLowerCase()}?`}
        confirmText="Delete"
        cancelText="Cancel"
        color="error"
        content={
          confirmContent ?? (
            <Typography variant="body2">
              This will permanently delete all existing {title.toLowerCase()} data. This action cannot be undone.
            </Typography>
          )
        }
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        disabled={isDeleting}
      />
    </>
  );
};

export default LayerDeleteAction;
